import React, { useEffect, useMemo, useState } from 'react';
import InventoryDashboard from "../components/inventory/InventoryDashboard";
import Menu from "../components/common/menu/Menu";
import Sidebar from "../components/common/menu/Sidebar";
import GrossProfitCard from "../components/inventory/analytics/GrossProfitCard";
import NetProfitCard from "../components/inventory/analytics/NetProfitCard";
import TotalSalesRevenueCard from "../components/inventory/analytics/TotalSalesRevenueCard";
import TotalInventoryCostCard from "../components/inventory/analytics/TotalInventoryCostCard";
import InventoryValueCard from "../components/inventory/analytics/InventoryValueCard";
import CostOfGoodsSoldCard from "../components/inventory/analytics/CostOfGoodsSoldCard";
import OperatingExpensesCard from "../components/inventory/analytics/OperatingExpensesCard";
import ProfitMarginCard from "../components/inventory/analytics/ProfitMarginCard";
import {Card, CardBody} from "@material-tailwind/react";
import InStockSummaryCard from "../components/inventory/InStockSummaryCard";
import TotalPersonalCostCard from "../components/inventory/analytics/TotalPersonalCostCard";
import ItemSalesSpeedCard from "../components/inventory/ItemSalesSpeedCard";
import FinancialReportCard from "../components/inventory/analytics/FinancialReportCard";
import DailyProfitCard from "../components/inventory/analytics/DailyProfitCard";
import axios from "axios";
import {isWorker} from "../utils/auth";


function InventoryPage() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [availableItems, setAvailableItems] = useState([]);
    const [soldItems, setSoldItems] = useState([]);
    const [personalItems, setPersonalItems] = useState([]);
    const [operatingExpenses, setOperatingExpenses] = useState([]);
    const now = new Date();
    const [analyticsPeriod, setAnalyticsPeriod] = useState({
        mode: 'month',
        year: now.getFullYear(),
        quarter: Math.floor(now.getMonth() / 3) + 1,
        month: now.getMonth() + 1,
    });
    const workerView = isWorker();

    const toggleSidebar = (isOpen) => {
        setSidebarOpen(isOpen);
    };

    const token = localStorage.getItem("token");
    const requestHeaders = useMemo(() => ({ 'Authorization': `Bearer ${token}` }), [token]);

    useEffect(() => {
        if (workerView) return;

        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";

        const fetchAnalyticsData = async () => {
            try {
                const [
                    availableItemsResponse,
                    soldItemsResponse,
                    personalItemsResponse,
                    operatingExpensesResponse,
                ] = await Promise.all([
                    axios.get(`${API_URL}/purchased_items/available_items`, { headers: requestHeaders }),
                    axios.get(`${API_URL}/purchased_items/sold_items`, { headers: requestHeaders }),
                    axios.get(`${API_URL}/purchased_items/personal_items`, { headers: requestHeaders }),
                    axios.get(`${API_URL}/operating_expense/all`, { headers: requestHeaders }),
                ]);

                setAvailableItems(availableItemsResponse.data || []);
                setSoldItems(soldItemsResponse.data || []);
                setPersonalItems(personalItemsResponse.data || []);
                setOperatingExpenses(operatingExpensesResponse.data || []);
            } catch (error) {
                console.error("Error fetching inventory analytics data:", error);
            }
        };

        fetchAnalyticsData();
    }, [requestHeaders, workerView]);

    const currentYear = now.getFullYear();
    const availableYears = Array.from(
        { length: currentYear - 2023 },
        (_, index) => 2024 + index
    );

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const updateAnalyticsPeriod = (updates) => {
        setAnalyticsPeriod((current) => {
            const next = {
                ...current,
                ...updates,
            };

            if (next.year === currentYear) {
                next.quarter = Math.min(next.quarter, Math.floor(now.getMonth() / 3) + 1);
                next.month = Math.min(next.month, now.getMonth() + 1);
            }

            return next;
        });
    };

    const isQuarterDisabled = (quarter, year) => {
        if (year < currentYear) return false;
        if (year > currentYear) return true;

        return quarter > Math.floor(now.getMonth() / 3) + 1;
    };

    const isMonthDisabled = (month, year) => {
        if (year < currentYear) return false;
        if (year > currentYear) return true;

        return month > now.getMonth() + 1;
    };

    return (
        <div className="overflow-hidden dark:bg-dark-950">
            <div className="block md:hidden">
                <Menu sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
            </div>

            <div className="hidden md:block ">
                <Sidebar />
            </div>

            <div className="md:ml-[15rem] bg-light-blue-50 relative dark:bg-dark-950">
                <div className="absolute inset-0 opacity-[0.04] dark:opacity-0" style={{
                    backgroundImage: 'url(https://i.ibb.co/9kYFDXtW/background-manga.jpg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}></div>

                <div className="relative">
                    <div className="flex justify-center items-center">
                        <div className="w-full mt-8 lg:mt-20 mb-56" style={{ maxWidth: '1420px' }}>
                            <div className="mx-2 lg:mx-10">
                                <InventoryDashboard fixedName={true} />
                            </div>

                            {!workerView && (
                                <>
                            <div className="mt-8 mx-2 lg:mx-10">
                                <div className="w-full rounded-lg bg-white dark:bg-dark-900 shadow-lg px-4 py-3">
                                    <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-200">Analytics period</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Applies to cards that support the new period view</p>
                                        </div>

                                        <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end">
                                            {analyticsPeriod.mode !== 'all_time' && (
                                                <select
                                                    value={analyticsPeriod.year}
                                                    onChange={(event) => updateAnalyticsPeriod({ year: Number(event.target.value) })}
                                                    className="w-full sm:w-28 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-dark-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-700"
                                                >
                                                    {availableYears.map((year) => (
                                                        <option key={year} value={year}>{year}</option>
                                                    ))}
                                                </select>
                                            )}

                                            {analyticsPeriod.mode === 'quarter' && (
                                                <select
                                                    value={analyticsPeriod.quarter}
                                                    onChange={(event) => updateAnalyticsPeriod({ quarter: Number(event.target.value) })}
                                                    className="w-full sm:w-24 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-dark-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-700"
                                                >
                                                    {[1, 2, 3, 4].map((quarter) => (
                                                        <option
                                                            key={quarter}
                                                            value={quarter}
                                                            disabled={isQuarterDisabled(quarter, analyticsPeriod.year)}
                                                        >
                                                            Q{quarter}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}

                                            {analyticsPeriod.mode === 'month' && (
                                                <select
                                                    value={analyticsPeriod.month}
                                                    onChange={(event) => updateAnalyticsPeriod({ month: Number(event.target.value) })}
                                                    className="w-full sm:w-36 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-dark-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-700"
                                                >
                                                    {months.map((month, index) => (
                                                        <option
                                                            key={month}
                                                            value={index + 1}
                                                            disabled={isMonthDisabled(index + 1, analyticsPeriod.year)}
                                                        >
                                                            {month}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}

                                            <div className="grid grid-cols-4 w-full sm:w-[356px] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-dark-800">
                                                {[
                                                    { value: 'all_time', label: 'All time' },
                                                    { value: 'year', label: 'Year' },
                                                    { value: 'quarter', label: 'Quarter' },
                                                    { value: 'month', label: 'Month' },
                                                ].map(({ value, label }) => (
                                                    <button
                                                        key={value}
                                                        type="button"
                                                        onClick={() => updateAnalyticsPeriod({ mode: value })}
                                                        className={`px-3 py-2 text-sm font-medium transition-colors ${
                                                            analyticsPeriod.mode === value
                                                                ? 'bg-cyan-800 text-white'
                                                                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700'
                                                        }`}
                                                    >
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 mb-8 mx-2 lg:mx-10 grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <Card className="w-full shadow-lg dark:bg-dark-900">
                                    <CardBody>
                                        <div className="flow-root">
                                            <ul className="divide-y divide-gray-200 dark:divide-gray-800">
                                                <InventoryValueCard
                                                    analyticsPeriod={analyticsPeriod}
                                                    onAnalyticsPeriodChange={updateAnalyticsPeriod}
                                                    providedAvailableItems={availableItems}
                                                    providedSoldItems={soldItems}
                                                />
                                                <TotalPersonalCostCard
                                                    analyticsPeriod={analyticsPeriod}
                                                    onAnalyticsPeriodChange={updateAnalyticsPeriod}
                                                    providedPersonalItems={personalItems}
                                                />
                                                <TotalInventoryCostCard
                                                    analyticsPeriod={analyticsPeriod}
                                                    onAnalyticsPeriodChange={updateAnalyticsPeriod}
                                                    providedAvailableItems={availableItems}
                                                    providedSoldItems={soldItems}
                                                />
                                                <TotalSalesRevenueCard
                                                    analyticsPeriod={analyticsPeriod}
                                                    onAnalyticsPeriodChange={updateAnalyticsPeriod}
                                                    providedSoldItems={soldItems}
                                                />
                                                <CostOfGoodsSoldCard
                                                    analyticsPeriod={analyticsPeriod}
                                                    onAnalyticsPeriodChange={updateAnalyticsPeriod}
                                                    providedSoldItems={soldItems}
                                                />
                                            </ul>
                                        </div>
                                    </CardBody>
                                </Card>

                                <Card className="w-full shadow-lg dark:bg-dark-900">
                                    <CardBody>
                                        <div className="flow-root">
                                            <ul className="divide-y divide-gray-200 dark:divide-gray-800">
                                                <GrossProfitCard
                                                    analyticsPeriod={analyticsPeriod}
                                                    onAnalyticsPeriodChange={updateAnalyticsPeriod}
                                                    providedSoldItems={soldItems}
                                                />
                                                <OperatingExpensesCard
                                                    analyticsPeriod={analyticsPeriod}
                                                    onAnalyticsPeriodChange={updateAnalyticsPeriod}
                                                    providedOperatingExpenses={operatingExpenses}
                                                />
                                                <NetProfitCard
                                                    analyticsPeriod={analyticsPeriod}
                                                    onAnalyticsPeriodChange={updateAnalyticsPeriod}
                                                    providedSoldItems={soldItems}
                                                    providedOperatingExpenses={operatingExpenses}
                                                />
                                                <ProfitMarginCard />
                                                <FinancialReportCard />
                                                <DailyProfitCard />
                                            </ul>
                                        </div>
                                    </CardBody>
                                </Card>
                            </div>

                            <div className="mt-8 mb-8 mx-2 lg:mx-10 grid grid-cols-1 lg:grid-cols-1 gap-4">
                                <InStockSummaryCard />
                            </div>

                            <div className="mx-2 lg:mx-10">
                                <ItemSalesSpeedCard/>

                            </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default InventoryPage;
